// Forms a single entry point for loading report data
// Simplifies the complex flow when the report dialog is loaded on
// a new station
// Dispatches all redux actions as a batch

import { batch } from 'react-redux';
import { AppThunk, RootState } from '../store';

// Stores
import { loadReports } from './reportsSlice'
import { loadReportTemplates } from './reportTemplatesSlice';
import { loadReportSchedules } from './reportSchedulesSlice';
import { loadCustomers } from './customersSlice';
import { cloneDeep } from 'lodash';

// API calls
import { getSiteCustomer, updateSiteCustomer } from '../../api/site';
import { getCustomer, createCustomer } from '../../api/customer';
import { createReportSchedule, getReportSchedules, setActive } from '../../api/reportSchedule';
import { getReports } from '../../api/report';
import { getReportTemplates } from '../../api/reportTemplate';
import { UsersSlice } from '../users/types';
import { ResolvedReportSlice, ResolvedReportScheduleSlice } from './types';

const resolveUsers = (users: Array<UsersSlice>, userIds: Array<number>): Array<UsersSlice> => {
  return userIds.map(userId => {
    return users.filter(user => user.id === userId)[0] || null;;
  })
}

export const selectResolvedReports = (state: RootState) => {
  return state.reports.map(report => {
    const resolvedTemplate = cloneDeep(state.reportTemplates.filter(reportTemplate => reportTemplate.report_template_id === report.template_id)[0]) || null;
    let resolvedSchedule: ResolvedReportScheduleSlice = cloneDeep(state.reportSchedules.filter(reportSchedule => reportSchedule.report_schedule_id === report.report_schedule_id)[0]) || null;
    if (resolvedSchedule !== null) {
      resolvedSchedule.resolvedRecipients = resolveUsers(state.users, resolvedSchedule.recipients);
    }
    const resolvedRecipients = resolveUsers(state.users, report.recipients);
    const resolvedCustomer = cloneDeep(state.customers.filter(customer => customer.id === report.customer_id)[0]) || null;
    const resolvedMenuNode = cloneDeep(state.menuNodes.filter(menuNode => parseInt(menuNode.id) === report.station_id)[0]) || null;
    
    return {
      ...report,
      resolvedTemplate,
      resolvedRecipients,
      resolvedSchedule,
      resolvedCustomer,
      resolvedMenuNode
    }
  })
}

export const selectResolvedSchedules = (state: RootState) => {
  return state.reportSchedules.map(schedule => {
    const resolvedTemplate = cloneDeep(state.reportTemplates.filter(reportTemplate => reportTemplate.report_template_id === schedule.template_id)[0]) || null;
    const resolvedRecipients = resolveUsers(state.users, schedule.recipients);
    const resolvedCustomer = cloneDeep(state.customers.filter(customer => customer.id === schedule.customer_id)[0]) || null;
    const resolvedMenuNode = cloneDeep(state.menuNodes.filter(menuNode => parseInt(menuNode.id) === schedule.station_id)[0]) || null;
    
    return {
      ...schedule,
      resolvedTemplate,
      resolvedRecipients,
      resolvedCustomer,
      resolvedMenuNode
    }
  })
}

export const fetchPositionsReportData = (): AppThunk => async (dispatch, getState) => {
  const { user, menuNodes } = getState();
  
  const stationIds = menuNodes.filter(node => node.type === 'station' && node.selected).map(node => node.id);
  
  try {
    const reports = await getReports(user.api_key, parseInt(stationIds[0]));
    const reportTemplates = await getReportTemplates(user.api_key);
    const scheduledReports = await getReportSchedules(user.api_key, parseInt(stationIds[0]));
    
    batch(() => {
      // dispatch(loadCustomers([customer]));
      dispatch(loadReports(reports.reverse()))
      dispatch(loadReportSchedules(scheduledReports))
      dispatch(loadReportTemplates(reportTemplates))
    })
    
  } catch (e) {
    console.error(e)
  }
}

export const fetchAllReportData = (station_id: number, site_id: number): AppThunk => async (dispatch, getState) => {
  const { user } = getState()
  
  try {
    const site_customer = await getSiteCustomer(user.api_key, site_id);
    const report_templates = await getReportTemplates(user.api_key, station_id);
    var customer = null;
    
    // Handle no customer
    if (site_customer.customer_id == null) {
      customer = await createCustomer(user.api_key, {
        name: 'Customer Name',
        address: `Address Line 1
Address Line 2
City
Postcode`,
      })
      await updateSiteCustomer(user.api_key, site_id, customer.customer_id)
      await createReportSchedule(user.api_key, {
        station_id: station_id,
        customer_id: customer.customer_id,
        recipients: [],
        template_id: report_templates[0].id
      })
    
    } else {
      customer = await getCustomer(user.api_key, site_customer.customer_id)
    }
    
    var scheduled_reports = await getReportSchedules(user.api_key, station_id);

    if (scheduled_reports.length == 0) {
      let sr = {
        station_id: station_id,
        customer_id: customer.customer_id,
        recipients: [],
        template_id: report_templates[0].id
      }
      let new_scheduled_report = await createReportSchedule(user.api_key, sr)
      await setActive(user.api_key, new_scheduled_report.report_schedule_id, 0)

      scheduled_reports = await getReportSchedules(user.api_key, station_id);
    }
    
    const reports = await getReports(user.api_key, station_id);
    
    batch(() => {
      dispatch(loadCustomers([customer]));
      dispatch(loadReports(reports.reverse()))
      dispatch(loadReportSchedules(scheduled_reports))
      dispatch(loadReportTemplates(report_templates))
    })
    
    // Example using promises
    
    // const calls = [
    //   await getReports(user.api_key, station_id),
    //   await getReportSchedules(user.api_key, station_id)
    // ]
    
    // Promise.all(calls)
    // .then(([reports, scheduled_reports]: [Report[], ReportSchedule[]]) => {
    // 
    //   if (scheduled_reports.length === 0) {
    // 
    //   }
    // 
    //   batch(() => {
    //     dispatch(loadCustomers([customer]));
    //     dispatch(loadReports(reports.reverse()))
    //     dispatch(loadReportSchedules(scheduled_reports))
    //   })
    // 
    // })

  } catch (err) {
    console.error(err)
  }
}